/**
 * In-App Authentication Modal
 * Modern approach: Keep users in the app when they need to re-authenticate
 */

import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useAuthInterceptor } from '../../services/api/authInterceptor';
import { useAuth } from '../../contexts/AuthContext';
import { navigationService } from '../../utils/navigationService';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  title?: string;
  message?: string;
}

export const AuthModal: React.FC<AuthModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  title = "Session Expired",
  message = "Please log in again to continue."
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const { forceRefresh } = useAuthInterceptor();
  const { revalidateAuth } = useAuth();

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setEmail('');
      setPassword('');
      setIsLoading(false);
      setShowForgotPassword(false);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const endpoint = isSignUp ? '/api/auth/register' : '/api/auth/login';
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (response.ok) {
        // With cookie-based auth, login/register sets HttpOnly cookies
        // Re-validate auth to update user state
        await revalidateAuth();

        toast.success(isSignUp ? 'Account created successfully!' : 'Welcome back!');
        onSuccess();
        onClose();
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || 'Authentication failed');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch('/api/auth/password-reset/request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        toast.success('Password reset email sent!');
        setShowForgotPassword(false);
      } else {
        toast.error('Failed to send reset email');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialLogin = async (provider: 'google' | 'github') => {
    setIsLoading(true);
    try {
      // Redirect to OAuth provider
      window.location.href = `/api/auth/${provider}`;
    } catch (error) {
      toast.error('Social login failed');
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="auth-modal-overlay">
      <div className="auth-modal">
        <div className="auth-modal-header">
          <h2>{title}</h2>
          <button 
            onClick={onClose}
            className="auth-modal-close"
            disabled={isLoading}
          >
            ×
          </button>
        </div>

        <div className="auth-modal-content">
          <p className="auth-modal-message">{message}</p>

          {showForgotPassword ? (
            <div className="forgot-password-form">
              <h3>Reset Password</h3>
              <p>Enter your email to receive a password reset link.</p>
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
              />
              <div className="auth-modal-actions">
                <button
                  onClick={handleForgotPassword}
                  disabled={isLoading || !email}
                  className="auth-button primary"
                >
                  {isLoading ? 'Sending...' : 'Send Reset Email'}
                </button>
                <button
                  onClick={() => setShowForgotPassword(false)}
                  disabled={isLoading}
                  className="auth-button secondary"
                >
                  Back to Login
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="auth-form">
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
              />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
              />

              <button
                type="submit"
                disabled={isLoading || !email || !password}
                className="auth-button primary"
              >
                {isLoading ? 'Signing in...' : (isSignUp ? 'Sign Up' : 'Sign In')}
              </button>

              <div className="auth-modal-actions">
                <button
                  type="button"
                  onClick={() => setIsSignUp(!isSignUp)}
                  disabled={isLoading}
                  className="auth-button text"
                >
                  {isSignUp ? 'Already have an account? Sign In' : "Don't have an account? Sign Up"}
                </button>
              </div>

              <div className="auth-divider">
                <span>or</span>
              </div>

              <div className="social-login">
                <button
                  type="button"
                  onClick={() => handleSocialLogin('google')}
                  disabled={isLoading}
                  className="social-button google"
                >
                  <span className="social-icon">🔍</span>
                  Continue with Google
                </button>
                <button
                  type="button"
                  onClick={() => handleSocialLogin('github')}
                  disabled={isLoading}
                  className="social-button github"
                >
                  <span className="social-icon">🐙</span>
                  Continue with GitHub
                </button>
              </div>

              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                disabled={isLoading}
                className="auth-button text"
              >
                Forgot your password?
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// Hook for managing auth modal state
export const useAuthModal = () => {
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalConfig, setAuthModalConfig] = useState({
    title: "Session Expired",
    message: "Please log in again to continue."
  });

  const openAuthModal = (config?: { title?: string; message?: string }) => {
    if (config) {
      setAuthModalConfig({
        title: config.title || "Session Expired",
        message: config.message || "Please log in again to continue."
      });
    }
    setIsAuthModalOpen(true);
  };

  const closeAuthModal = () => {
    setIsAuthModalOpen(false);
  };

  const handleAuthSuccess = () => {
    // Use navigation service to reload/refresh app state
    // On web: this will trigger a state refresh
    // On mobile: this will update app state without reload
    navigationService.reload();
  };

  return {
    isAuthModalOpen,
    authModalConfig,
    openAuthModal,
    closeAuthModal,
    handleAuthSuccess,
  };
};
